import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import LeadCard from "@/components/feature/LeadCard";
import { categories, leads } from "@/mocks/leads";
import { useSeo, BASE_URL } from "@/hooks/useSeo";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function Leads() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("c") ?? "all";
  const initialQ = params.get("q") ?? "";

  const [activeCat, setActiveCat] = useState(initialCat);
  const [keyword, setKeyword] = useState(initialQ);
  const [query, setQuery] = useState(initialQ);

  useScrollReveal();

  const filtered = useMemo(() => {
    const kw = query.trim().toLowerCase();
    return leads.filter((lead) => {
      const matchCat = activeCat === "all" || lead.category === activeCat;
      const matchKw =
        !kw ||
        lead.title.toLowerCase().includes(kw) ||
        lead.summary.toLowerCase().includes(kw) ||
        lead.tags.some((t) => t.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
  }, [activeCat, query]);

  const syncUrl = (cat: string, kw: string) => {
    const next = new URLSearchParams();
    if (cat !== "all") next.set("c", cat);
    if (kw.trim()) next.set("q", kw.trim());
    setParams(next, { replace: true });
  };

  const handleSelectCat = (id: string) => {
    setActiveCat(id);
    syncUrl(id, query);
  };

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setQuery(keyword);
    syncUrl(activeCat, keyword);
  };

  const activeName =
    activeCat === "all" ? "全部线索" : categories.find((c) => c.id === activeCat)?.name ?? "全部线索";

  useSeo({
    title: `${activeName} - 蝌蚪爆料线索库｜投稿线索与内幕材料索引`,
    description:
      "蝌蚪爆料线索库按主题整理已公开的投稿线索，涵盖消费维权、商业观察、职场一线、行业调查、环境民生与校园信息。支持关键词搜索与栏目筛选，每条线索均标注核实状态。",
    keywords: "蝌蚪爆料线索,爆料线索库,线索投稿,内幕材料,消费维权线索,行业调查",
    path: "/leads",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "蝌蚪爆料线索库",
        url: `${BASE_URL}/leads`,
        inLanguage: "zh-CN",
        description: "按主题整理的投稿线索索引，支持关键词搜索与栏目筛选。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: BASE_URL },
          { "@type": "ListItem", position: 2, name: "线索库", item: `${BASE_URL}/leads` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: filtered.slice(0, 10).map((lead, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: lead.title,
          url: `${BASE_URL}/leads/${lead.id}`,
        })),
      },
    ],
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100 py-10 md:py-14">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "线索库" }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-5xl">
            线索库
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里集中呈现蝌蚪爆料已完成初步核实的投稿线索。每条内容都标注了核实状态与来源类型，
            你可以按栏目浏览，也可以直接搜索关心的关键词，找到与你处境相近的真实经历。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => handleSelectCat("all")}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCat === "all"
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-50 text-foreground-700 hover:text-foreground-950"
                }`}
              >
                全部
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => handleSelectCat(c.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeCat === c.id
                      ? "bg-foreground-950 text-background-50"
                      : "bg-background-50 text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearch} className="flex w-full items-center gap-2 lg:w-auto">
              <div className="relative flex-1 lg:w-72">
                <i
                  className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索线索关键词，如 预付卡"
                  aria-label="搜索线索关键词"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
              <button
                type="submit"
                className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-heading font-bold text-foreground-950">{filtered.length}</span> 条线索
              {activeCat !== "all" && <span className="ml-1">· 栏目「{activeName}」</span>}
              {query.trim() && <span className="ml-1">· 关键词「{query.trim()}」</span>}
            </p>
            {(activeCat !== "all" || query.trim()) && (
              <button
                type="button"
                onClick={() => {
                  setActiveCat("all");
                  setKeyword("");
                  setQuery("");
                  syncUrl("all", "");
                }}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-500 transition-colors hover:text-primary-600"
              >
                <i className="ri-refresh-line" aria-hidden="true" />
                重置筛选
              </button>
            )}
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((lead) => (
                <div key={lead.id} className="reveal">
                  <LeadCard lead={lead} />
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-2-line text-2xl" aria-hidden="true" />
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                暂时没有匹配的线索
              </h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                试试换一个关键词，或者浏览「全部」栏目。如果你掌握的正是这方面的情况，欢迎直接投稿，我们会优先跟进。
              </p>
              <Link
                to="/guide#submit"
                className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-2-line" aria-hidden="true" />
                投稿这条线索
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}